import {RealTimeCountStatusEnum} from "../../../enum/real-time-count-status.enum";
import {SelectionTargetTypeEnum} from "../../../enum/selection-target-type.enum";
import {RuleGroupNode, SelectionCriteriaTree} from "./selection-criteria-tree.interface";

/**
 * Request DTO for real-time count estimation.
 * Used when criteria are being built and counts need to be updated live.
 */
export interface RealTimeCountRequest {
    selectionCriteriaId?: number;
    campaignId?: number;
    targetType?: SelectionTargetTypeEnum;
    criteriaTree?: SelectionCriteriaTree;
    rootGroups?: RuleGroupNode[];
    useCache?: boolean;
    timeoutMs?: number;
    triggerType?: string;
    isDebounced?: boolean;
}

/**
 * Response DTO for real-time count estimation.
 * Provides live member count updates with delta indicators.
 * Implements F-025: Real-Time Member Count Updates.
 */
export interface RealTimeCountResponse {
    estimateId: number;
    selectionCriteriaId: number;
    status: RealTimeCountStatusEnum;
    estimatedCount: number;
    previousCount?: number;
    countDelta?: number;
    deltaIndicator?: DeltaIndicator;
    executionTimeMs: number;
    isCached: boolean;
    confidenceLevel?: number;
    isEstimation?: boolean;
    requestedAt: Date;
    completedAt?: Date;
    expiresAt?: Date;
    errorMessage?: string;
    criteriaHash?: string;
    performanceIndicator?: PerformanceIndicator;
}

/**
 * Delta indicator for UI display.
 */
export interface DeltaIndicator {
    type: 'increase' | 'decrease' | 'no_change' | 'unknown';
    color: string;
    icon: string;
}

/**
 * Performance indicator for UI display.
 */
export interface PerformanceIndicator {
    level: 'fast' | 'normal' | 'slow' | 'very_slow';
    color: string;
    description: string;
}

/**
 * Trigger types for real-time count requests.
 */
export type RealTimeCountTriggerType =
    | 'CRITERIA_CHANGE'
    | 'FILTER_ADD'
    | 'FILTER_REMOVE'
    | 'FILTER_UPDATE'
    | 'GROUP_ADD'
    | 'GROUP_REMOVE'
    | 'SEGMENT_ADD'
    | 'SEGMENT_REMOVE'
    | 'MANUAL_REFRESH'
    | 'CAMPAIGN_VIEW';

/**
 * Configuration for real-time count updates.
 */
export interface RealTimeCountConfig {
    enabled: boolean;
    debounceMs: number;
    cacheEnabled: boolean;
    autoRefreshOnChange: boolean;
    showDeltaIndicator: boolean;
    showPerformanceIndicator: boolean;
}

/**
 * Default configuration for real-time count updates.
 */
export const DEFAULT_REAL_TIME_COUNT_CONFIG: RealTimeCountConfig = {
    enabled: true,
    debounceMs: 500,
    cacheEnabled: true,
    autoRefreshOnChange: true,
    showDeltaIndicator: true,
    showPerformanceIndicator: true
};
